import {
  CtaState,
  DenominationGridInternalState,
} from "~/components/denomination_grid/denominations_grid";
import { useAnalyticsService } from "~/context/analytics_service_context";
import { HubbleEvent } from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { PreReviewPopupDetails, VoucherProduct } from "~/server/types/brand";
import {
  openRazorpayCheckout,
  RazorpayCheckoutProps,
  RazorpayMode,
} from "~/utils/third_party/razorpay";
import { showSnackBar } from "~/shared_states/snackbar";
import { useWebStoreContext } from "~/context/webstore_context";
import { config } from "~/data/config";
import { closeAllModals, goBack } from "~/shared_states/modal";
import { createSignal } from "solid-js";
import { Header } from "~/types";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { ErrorMessage } from "~/data/consts";
import { generateDeviceVerificationToken } from "~/utils/common";
import { useModal } from "~/components/modal";
import { openPaymentLoaderTelescope } from "~/components/webstore_telescope/payment_loader_telescope";
import { useNavigate } from "@solidjs/router";
import { openWarningModalTelescope } from "~/components/webstore_telescope/warning_modal_telescope";
import { openOrderFailedModal } from "~/components/webstore_telescope/order_failed_modal";

export interface OpenPaymentLoaderProps {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}

// this hook uses webstore context, should not be used outside of webstore
export function useWebstorePayment(props: { brand: () => VoucherProduct }) {
  const [ctaState, setCtaState] = createSignal<CtaState>("DEFAULT");
  const [showFullPageLoadingOverlay] = createSignal(false);
  const navigate = useNavigate();
  const modalContext = useModal();

  const brand = props.brand;
  // Store current internal state from denomination grid
  let currentInternalState: DenominationGridInternalState | null = null;

  const analyticsService = useAnalyticsService();

  const { storeState, loginToWebstore } = useWebStoreContext();

  // used to override the default denomination list
  function getDenominationList(_brand: VoucherProduct) {
    return undefined;
  }

  const onClickGetThisCard: (
    internalState: DenominationGridInternalState
  ) => Promise<void> = async (internalState: DenominationGridInternalState) => {
    // Store internal state for use in order initiation
    currentInternalState = internalState;

    if (!storeState.isLoggedIn) {
      await loginToWebstore(onAfterLogin);
    } else {
      await onAfterLogin();
    }
  };

  async function onAfterLogin() {
    const preReviewPopupDetails =
      brand()?.voucherProductMetadata.preReviewPopupDetails;

    if (preReviewPopupDetails != null) {
      openWarningModal({
        onContinue: async () => {
          await closeAllModals();
          await initiateOrder();
        },
        preReviewPopupDetails: preReviewPopupDetails,
        modalContext: modalContext,
      });
    } else {
      await initiateOrder();
    }
  }

  const initiateOrder = async () => {
    try {
      setCtaState("LOADING");

      // Use amount from internal state if available, fallback to simulation data
      const amount = currentInternalState?.redemptionAmount();
      // TODO: add a logic to get fallback or make currentInternalState non nullable
      // simulateTransactionDetails()?.redemptionAmount;
      if (!amount) {
        throw new Error("Can't initiate order when amount is null");
      }

      let headers: Record<string, any> = {};
      if (storeState.v4HomePage?.user.fraudChecksEnabled) {
        const token = await generateDeviceVerificationToken();
        if (!token) {
          const err = new Error("FPJS token is undefined");
          captureErrorInSentry(err);
          showSnackBar({
            message: ErrorMessage.SECURITY_CHECK_FAILED,
            level: "error",
          });
          throw err;
        }
        headers[Header.FpDeviceToken] = token;
      }

      const initiateRedemptionResponse = await clientRepo.initiateRedemption(
        {
          amount: amount,
          paymentMode: "UPI",
          productId: brand()?.externalId!,
          ...(brand()?.amountConditions.denominations != null
            ? {
                denominationDetails: [
                  {
                    denomination: amount,
                    quantity: 1,
                  },
                ],
              }
            : {}),
          variantId:
            currentInternalState?.selectedVariantId() ??
            brand()?.variantDetailedResponse?.find(
              (variant) => variant.isDefault
            )?.variantId ??
            "",
          consumeCoins: currentInternalState?.useRewardCoins() ?? false,
        },
        headers
      );

      analyticsService.logEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: brand()!.name.toLowerCase(),
        amount: amount,
        consumeCoins: currentInternalState?.useRewardCoins() ?? false,
        productId: brand()!.externalId,
      });

      function onPaymentFailed(failureReason: string) {
        analyticsService.logEvent(HubbleEvent.PAYMENT_FAIL, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
          reason: failureReason,
        });

        // Open modal instead of navigating
        openOrderFailedModal({
          reason: failureReason,
        });
      }

      function onPaymentComplete() {
        analyticsService.logEvent(HubbleEvent.PAYMENT_SUCCESS, {
          brandName: brand()!.name.toLowerCase(),
          amount: amount,
        });

        const orderId = encodeURIComponent(
          initiateRedemptionResponse.storeOrderDetails.id
        );
        const brandKey = encodeURIComponent(brand()!.brandKey);

        navigate(`/buy-gift-card/${brandKey}/order?orderId=${orderId}`, {
          resolve: true,
        });
      }

      function collectPaymentViaCustomCheckout() {
        openPaymentLoader({
          amount: initiateRedemptionResponse.depositPaymentDetails.amount,
          onPaymentComplete: async () => {
            goBack();
            await new Promise((resolve) => setTimeout(resolve, 300));
            onPaymentComplete();
          },
          onPaymentFailed: onPaymentFailed,
          orderId: initiateRedemptionResponse.depositPaymentDetails.id,
          reInit: initiateOrder,
          onClose: () => {
            goBack();
          },
          brandImageUrl: brand()!.voucherProductMetadata.logoUrl,
        });
      }

      async function collectPaymentViaRazorpay(_props: {
        depositId: string;
        amount: number;
      }) {
        const order = await clientRepo.payOrderStandardCheckout(
          _props.depositId
        );

        let razorpayCheckoutParams: RazorpayCheckoutProps = {
          amount: _props.amount,
          email: storeState.v4HomePage?.user?.email ?? "",
          orderId: order
            ? order.providerOrderId
            : (initiateRedemptionResponse?.depositPaymentDetails
                .providerMetadata?.providerOrderId ?? ""),
          phone: storeState.v4HomePage?.user?.phoneNumber ?? "",
          onSuccess: onPaymentComplete,
          onFailure: (response) => {
            onPaymentFailed(response.error.description);
          },
          mode: "UPI" as RazorpayMode,
        };

        openRazorpayCheckout(razorpayCheckoutParams);
      }

      console.log(
        `init redemption response - provider is ${initiateRedemptionResponse.depositPaymentDetails.provider}`
      );

      if (
        initiateRedemptionResponse.depositPaymentDetails.status == "SUCCESS"
      ) {
        onPaymentComplete();
      } else {
        // This was done specifically for Raisoni portal where we didn't have Rzp whitelist
        // the portal url. Can be removed once Rzp is stable.
        if (config.webstore.paymentGateway === "razorpay") {
          await collectPaymentViaRazorpay({
            depositId: initiateRedemptionResponse.depositPaymentDetails.id,
            amount: initiateRedemptionResponse.depositPaymentDetails.amount,
          });
        } else {
          collectPaymentViaCustomCheckout();
        }
      }
      // State management moved to denomination grid
      setCtaState("DEFAULT");
    } catch (error: any) {
      showSnackBar({
        message: error.message,
        level: "error",
      });
      setCtaState("DEFAULT");

      // State management moved to denomination grid
    }
  };

  return {
    onClickGetThisCard,
    getDenominationList,
    showFullPageLoadingOverlay,
    ctaState,
    setCtaState,
  };
}

function openWarningModal(props: {
  onContinue: () => void;
  preReviewPopupDetails: PreReviewPopupDetails;
  modalContext: ReturnType<typeof useModal>;
}) {
  openWarningModalTelescope({
    onContinue: props.onContinue,
    preReviewPopupDetails: props.preReviewPopupDetails,
  });
}

function openPaymentLoader(props: {
  amount: number;
  onPaymentComplete: () => void;
  onPaymentFailed: (failureReason: string) => void;
  orderId: string;
  reInit: () => void;
  onClose: () => void;
  brandImageUrl: string;
}) {
  openPaymentLoaderTelescope({
    amount: props.amount,
    onPaymentComplete: props.onPaymentComplete,
    onPaymentFailed: props.onPaymentFailed,
    orderId: props.orderId,
    reInit: props.reInit,
    onClose: props.onClose,
    brandImageUrl: props.brandImageUrl,
  });
}
